/**
 * 发布采集页面
 *
 * @description
 * 该页面用于创建和发布采集（Pin），支持以下功能：
 * - 批量图片上传（最多50张，单张不超过20MB）
 * - 图片拖拽上传和粘贴上传
 * - 为每张图片设置描述、标签、来源链接
 * - AI生成内容标记和分类
 * - 选择目标画板
 * - 实时上传进度显示
 *
 * @architecture
 * 数据流：UI Components → updateFormValues → uploadResources → API
 * 使用受控组件模式，数据源统一为 uploadResources 数组
 */

import ActivitySelect from "@/components/ActivitySelect";
import { AigcSwitch } from "@/components/AigcSwitch";
import { openAiPublishConfirm } from "@/components/AiPublishConfirm";
import BoardSelect from "@/components/BoardSelect";
import HBButton from "@/components/HBButton";
import HBIcon from "@/components/HBIcon";
import HBMessage from "@/components/HBMessage";
import HBTagInput from "@/components/HBTagInput";
import Image from "@/components/Image";
import LoginDialog from "@/components/LoginModal";
import { PinPublishSucessMessage } from "@/components/PinSuccessMessage/pinPublish";
import uploadAccept, { MaxUploadAmount } from "@/constants/uploadAccept";
import useAppStore from "@/context/store";
import ImageAvatar from "@/pageComponents/PinCreate/ImageAvatar";
import { WarpBoardProgress } from "@/pageComponents/PinCreate/Progress";
import { STATUS as UPLOAD_STATUS } from "@/pageComponents/PinCreate/shared";
import {
  createPins,
  getBoard,
  getGrocery,
  getMyMuseStatus,
  getTags,
  uploadFile,
} from "@/services";
import { uploadTracker } from "@/tracker/report/uploadTtacker";
import { parallel } from "@/utils/concurrency";
import { gdSlsReportError } from "@/utils/gdSlsRum";
import imageUrl from "@/utils/imageUrl";
import { openBindTelModal } from "@/utils/open";
import useReq from "@/utils/useReq";
import {
  useAsyncEffect,
  useDebounceFn,
  useEventListener,
  useMemoizedFn,
  useSetState,
  useUnmount,
  useUpdate,
} from "ahooks";
import { Checkbox, Input, Select, Upload } from "antd";
import { type RcFile } from "antd/es/upload/interface";
import classNames from "classnames";
import { assign } from "lodash";
import Head from "next/head";
import { useEffect, useMemo, useRef, useState } from "react";
import { When } from "react-if";
import styles from "./index.module.less";
const { Dragger } = Upload;

/**
 * 判断文件是否为 PSD 格式
 * @param file - 文件对象
 * @returns 是否为 PSD 文件
 */
function isPSDByExtension(file) {
  const fileName = file.name.toLowerCase();
  return fileName.endsWith(".psd");
}

/**
 * 发布采集页面主组件
 * @returns 发布采集页面 JSX
 */
export default function CreatePage() {
  // 获取用户信息和状态
  const { user, userReady } = useAppStore((s) => ({
    user: s.user,
    userReady: s.userReady,
  }));

  // 未登录用户访问时，弹出登录对话框
  useEffect(() => {
    if (userReady && !user) {
      LoginDialog.open({
        source_location: "创建页面访问",
      });
    }
  }, [user, userReady]);

  /**
   * 灵感创建成功的回调
   * 清空文件列表和临时表单值，显示成功消息
   */
  const onPinCreateSucess = useMemoizedFn(async (board) => {
    setFileList([]);
    setTempFormValues(null);
    PinPublishSucessMessage.open({
      pin: { ...board?.pins?.shift(), board } as IPin,
    });
  });

  // 使用自定义 Hook 管理创建页面状态
  const {
    uploadResources, // 上传资源列表
    setFileList, // 设置待上传文件列表
    onRemove, // 移除资源
    globalValue, // 全局值（画板ID、标签等）
    setGlobalValue, // 设置全局值
    updateResource, // 更新单个资源
    onCreatePins, // 发布采集
    tagResult, // 标签列表
    selected, // 当前选中的资源
    setSelected, // 设置选中的资源
  } = useCreatePage({
    onSucess: onPinCreateSucess,
  });

  /**
   * 上传前的处理，阻止默认上传行为
   * @returns false - 阻止默认上传
   */
  const beforeUpload = useMemoizedFn(async (_, fileList: RcFile[]) => {
    if (fileList.every((file) => file.uid)) {
      setFileList(fileList);
    }
    return false;
  });

  /** 表单值类型定义 */
  type FormValues = {
    description: string; // 描述
    tags: string[]; // 标签数组
    link: string; // 来源链接
    user_checked_aigc: boolean; // 用户是否手动标记为 AI 生成
    aigc_category: string; // AI 工具分类
    aigc_prompt: string; // AI 提示词
    board_id?: number; // 画板 ID
    activity_id?: string; // 活动 ID
  };

  // 临时表单值（用于未上传图片时保存用户表单输入）
  const [tempFormValues, setTempFormValues] = useState<FormValues | null>(null);

  // 用户是否勾选了 AIGC 标记
  const [userCheckedAigc, setUserCheckedAigc] = useState(false);

  // AIGC 相关字段是否可编辑（如果已有 OSS 返回的 AIGC 信息则不可修改）
  const aigcPromptEditable = !selected?.ossFile?.aigc?.prompt;
  const aigcCategoryEditable = !selected?.ossFile?.aigc?.aigc_category;

  /**
   * 计算当前表单值
   * 优先级：selected > tempFormValues > 默认值
   * 使用 useMemo 缓存计算结果，避免不必要的重渲染
   */
  const currentFormValues = useMemo<FormValues>(() => {
    if (selected) {
      // 选中了图片，从 selected 获取值
      return {
        description: selected.description || "",
        tags:
          globalValue.tags?.length > 0 ? globalValue.tags : selected.tags || [],
        link: selected.link || "",
        user_checked_aigc: !!selected.user_checked_aigc,
        aigc_category: selected.aigc?.aigc_category || "",
        aigc_prompt: selected.aigc?.prompt || "",
        board_id: globalValue.boardId,
        activity_id: selected.activity_id,
      };
    } else if (tempFormValues) {
      // 未选中图片，但有临时值
      return {
        ...tempFormValues,
        board_id: globalValue.boardId,
      };
    } else {
      // 默认值
      return {
        description: "",
        tags: [],
        link: "",
        user_checked_aigc: false,
        aigc_category: "",
        aigc_prompt: "",
        board_id: globalValue.boardId,
        activity_id: undefined,
      };
    }
  }, [
    selected,
    uploadResources,
    tempFormValues,
    globalValue.tags,
    globalValue.boardId,
  ]);

  /**
   * 更新表单值
   * 根据是否选中图片，决定更新到 resource 还是 tempFormValues
   * @param changedValues - 变更的表单值
   */
  const updateFormValues = useMemoizedFn(
    (changedValues: Partial<FormValues>) => {
      // 更新全局画板 ID
      if (changedValues.board_id) {
        setGlobalValue({ boardId: changedValues.board_id });
      }

      // 如果开启了标签同步，更新全局标签
      if (changedValues.tags && globalValue.syncTagsChecked) {
        setGlobalValue({ tags: changedValues.tags });
      }

      // 更新 AIGC 勾选状态
      if ("user_checked_aigc" in changedValues) {
        setUserCheckedAigc(changedValues.user_checked_aigc as boolean);
      }

      if (selected) {
        // 选中了图片，更新到对应的 resource
        const updatedValues: Partial<UploadResource> = {};

        // 更新基本字段
        if ("description" in changedValues) {
          updatedValues.description = changedValues.description as string;
        }
        if ("tags" in changedValues) {
          updatedValues.tags = changedValues.tags as string[];
        }
        if ("link" in changedValues) {
          updatedValues.link = changedValues.link as string;
        }
        if ("user_checked_aigc" in changedValues) {
          updatedValues.user_checked_aigc =
            changedValues.user_checked_aigc as boolean;
        }
        if ("activity_id" in changedValues) {
          updatedValues.activity_id = changedValues.activity_id as string;
        }

        // 处理 AIGC 相关字段（合并更新）
        if (
          "aigc_category" in changedValues ||
          "aigc_prompt" in changedValues ||
          "user_checked_aigc" in changedValues
        ) {
          updatedValues.aigc = {
            ...selected.aigc,
            ...(changedValues.aigc_category && {
              aigc_category: changedValues.aigc_category,
            }),
            ...(changedValues.aigc_prompt && {
              prompt: changedValues.aigc_prompt,
            }),
            ...(changedValues.user_checked_aigc &&
              changedValues.aigc_category && {
                aigc_category: changedValues.aigc_category,
              }),
          };
        }

        updateResource(selected, updatedValues);
      } else {
        // 未选中图片，保存到临时状态
        setTempFormValues(
          (prev) => ({ ...prev, ...changedValues } as FormValues)
        );
      }
    }
  );

  /**
   * 当选中图片变化时，应用临时表单值到该图片
   * 这样可以保证用户在未选中图片时输入的内容不会丢失
   */
  useEffect(() => {
    if (selected && tempFormValues) {
      updateResource(selected, {
        description: tempFormValues.description,
        tags: tempFormValues.tags,
        link: tempFormValues.link,
        user_checked_aigc: tempFormValues.user_checked_aigc,
        activity_id: tempFormValues.activity_id,
        aigc: {
          ...selected.aigc,
          aigc_category: tempFormValues.aigc_category,
          prompt: tempFormValues.aigc_prompt,
        },
      });
      setTempFormValues(null);
    }
  }, [selected, tempFormValues, updateResource]);

  /**
   * 同步 userCheckedAigc 状态到当前选中的图片
   */
  useEffect(() => {
    if (selected) {
      setUserCheckedAigc(selected.user_checked_aigc);
    }
  }, [selected]);

  /**
   * 发布按钮点击处理
   * 如果检测到 AI 生成内容，需要用户确认后才能发布
   */
  const onPublishClick = useMemoizedFn(async () => {
    // 如果上传资源有 aigc （不包括用户手动添加的 aigc）则需要确认是否发布
    const isAigc = uploadResources.some((item) => item.ossFile?.aigc);
    if (isAigc && !(await openAiPublishConfirm())) {
      return;
    }
    onCreatePins();
  });

  // 判断是否为空（无上传资源）
  const isEmpty = !(uploadResources.length > 0);
  // 判断是否全部上传完成
  const allUploaded = uploadResources.every(
    (item) => item.status === UPLOAD_STATUS.DONE
  );

  const boardId = globalValue.boardId;
  const headerHeight = useAppStore((s) => s.headerHeight);

  // 1 表示检测到 AI 生成（OSS 返回）。上传场景只要检测到都算是检测到 AI 生成
  const originAigc = selected?.ossFile?.aigc ? 1 : undefined;
  // 是否可以修改 AIGC 标记（只有未检测到 AI 生成时才能修改）
  const isAigcChangeable = !originAigc;

  return (
    <div className={styles.page}>
      <Head>
        <title>发布采集-花瓣网</title>
      </Head>
      <div className={styles.container}>
        {/* 左侧内容区：图片预览和表单 */}
        <div className={styles.leftWrap} style={{ top: headerHeight + 20 }}>
          {/* 头部：标题和发布按钮 */}
          <div className={styles.header}>
            <h1 className={styles.title}>发布采集</h1>
            <HBButton
              className={styles.publishButton}
              onClick={onPublishClick}
              // 禁用条件：无资源 或 未全部上传完成 或 未选择画板
              disabled={isEmpty || !allUploaded || !boardId}
              data-gd-click="button_click"
              data-button-name="发布"
              data-activity-id={currentFormValues.activity_id}
              type="primary"
            >
              发布
            </HBButton>
          </div>

          <div className={styles.content}>
            {/* 图片预览区 */}
            <div
              className={classNames(styles.imageContainer, {
                [styles.imageContainerEmpty]: !selected?.url,
              })}
            >
              {selected?.url ? (
                // 显示选中图片及上传进度
                <WarpBoardProgress progress={selected.progress}>
                  <div className={styles.image}>
                    {selected.url && (
                      <img
                        className="transparent-img-bg"
                        src={
                          // PSD 文件使用 OSS 处理后的图片，其他格式使用原始预览 URL
                          isPSDByExtension(selected.file)
                            ? imageUrl(selected.ossFile, {
                                width: 240,
                              })
                            : selected.url
                        }
                        alt="图片"
                      />
                    )}
                  </div>
                  {/* 上传失败时显示错误信息 */}
                  {selected.failMsg && (
                    <div className={classNames(styles.failMsg, styles.mask)}>
                      <HBIcon type="ic_alert" />
                      {selected.failMsg}
                    </div>
                  )}
                </WarpBoardProgress>
              ) : (
                // 无选中图片时显示上传区域
                <Dragger
                  beforeUpload={beforeUpload}
                  accept={uploadAccept}
                  multiple
                  fileList={[]}
                  showUploadList={false}
                >
                  <Empty />
                </Dragger>
              )}
            </div>

            {/* 表单区域：使用受控组件模式 */}
            <div className={styles.formContainer}>
              <div className={styles.form}>
                {/* 画板选择器 */}
                <BoardSelect
                  className={classNames(styles.createSelect, "black-input")}
                  placeholder={"* 选择画板"}
                  getPopupContainer={(node) => node.parentNode}
                  emptyBoardsExpand={false}
                  selectFirst
                  useCreation
                  isNew
                  value={currentFormValues.board_id}
                  onChange={(value) => updateFormValues({ board_id: value })}
                />
                {/* 描述输入框 */}
                <Input.TextArea
                  className={"black-input"}
                  placeholder="添加描述"
                  maxLength={255}
                  style={{ height: 154 }}
                  value={currentFormValues.description}
                  onChange={(e) =>
                    updateFormValues({ description: e.target.value })
                  }
                />
                {/* 活动选择器 */}
                <ActivitySelect
                  className={"black-input"}
                  placeholder="选择活动"
                  value={currentFormValues.activity_id}
                  onChange={(value) => updateFormValues({ activity_id: value })}
                />
                {/* 标签输入器 */}
                <HBTagInput
                  suggestVisible={true}
                  className={styles.tagInput}
                  suggestTags={tagResult?.recent}
                  allTags={tagResult?.tags}
                  placeholder={"标签"}
                  useLabel={false}
                  isNew={true}
                  open={false}
                  value={currentFormValues.tags}
                  onChange={(value) => updateFormValues({ tags: value })}
                >
                  {/* 标签同步到全部采集的选项 */}
                  {(selected?.tags?.length > 0 ||
                    globalValue?.tags?.length > 0) && (
                    <div
                      className={classNames(
                        styles.checkbox,
                        styles.tagsCheckbox
                      )}
                    >
                      <Checkbox
                        checked={globalValue.syncTagsChecked}
                        onChange={() => {
                          setGlobalValue({
                            syncTagsChecked: !globalValue.syncTagsChecked,
                            tags: !globalValue.syncTagsChecked
                              ? selected.tags
                              : [],
                          });
                        }}
                      >
                        标签应用到全部采集
                      </Checkbox>
                    </div>
                  )}
                </HBTagInput>
                {/* 来源链接输入框 */}
                <Input
                  placeholder="添加来源网站链接"
                  className="black-input"
                  value={currentFormValues.link}
                  onChange={(e) => updateFormValues({ link: e.target.value })}
                />
                {/* AI 生成内容标记开关 */}
                <AigcSwitch
                  disabled={!selected || !isAigcChangeable}
                  originAigc={originAigc}
                  value={currentFormValues.user_checked_aigc}
                  onChange={(value) =>
                    updateFormValues({ user_checked_aigc: value })
                  }
                />
                {/* 当勾选了 AIGC 时显示工具选择和提示词输入 */}
                <When condition={!!selected && userCheckedAigc}>
                  <ToolSelect
                    disabled={!aigcCategoryEditable}
                    value={currentFormValues.aigc_category}
                    onChange={(value) =>
                      updateFormValues({ aigc_category: value })
                    }
                  />
                  <Input.TextArea
                    className={"black-input"}
                    maxLength={2000}
                    disabled={!aigcPromptEditable}
                    placeholder="添加提示词 Prompt"
                    value={currentFormValues.aigc_prompt}
                    onChange={(e) =>
                      updateFormValues({ aigc_prompt: e.target.value })
                    }
                  />
                </When>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* 右侧：上传资源列表 */}
      <div
        className={classNames(
          !(uploadResources?.length > 0) && styles.noContent,
          styles.createList
        )}
      >
        {/* 添加更多图片按钮 */}
        {uploadResources?.length > 0 && (
          <Dragger
            className={styles.add}
            beforeUpload={beforeUpload}
            accept={uploadAccept}
            multiple
            fileList={[]}
            showUploadList={false}
          >
            <HBIcon type="plus" />
          </Dragger>
        )}
        {/* 已上传的图片列表 */}
        {uploadResources?.map((item) => (
          <ImageAvatar
            resource={item}
            selected={item.uid === selected?.uid}
            key={item.uid}
            onSelect={setSelected}
            onRemove={(item: UploadResource) => {
              onRemove(item);
              // 如果删除的是当前选中的图片，自动选择下一张
              if (item.uid === selected?.uid) {
                let nextValue = uploadResources[0] || null;
                if (nextValue?.uid === selected?.uid) {
                  nextValue = uploadResources[1] || null;
                }
                setSelected(nextValue);
              }
            }}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * 空状态组件
 * 显示在无图片时的上传提示
 */
function Empty() {
  return (
    <div className={styles.empty}>
      <div className={styles.add}>
        <Image
          className={styles.addIcon}
          src="https://grocery-cdn.huaban.com/file/hb_add_icon.svg"
          width={33}
          height={33}
          alt="addicon"
        />

        <Image
          className={styles.uploadIcon}
          src={"https://grocery-cdn.huaban.com/file/hb_upload_icon.svg"}
          width={40}
          height={40}
          alt="addicon"
        />

        <div>拖放文件或点击添加</div>
      </div>
      <div className={styles.desc}>
        每次最多上传 50 张, 单张图片不超过 20 MB
      </div>
    </div>
  );
}

/**
 * 发布采集页面逻辑 Hook
 *
 * @description
 * 管理发布采集页面的核心逻辑，包括：
 * - 文件上传
 * - 粘贴上传
 * - 资源选择
 * - 画板和标签管理
 *
 * @param onSucess - 创建成功的回调
 * @returns 发布采集页面所需的状态和方法
 */
function useCreatePage({
  onSucess,
}: {
  onSucess?: (board: IBoardInfo) => void;
}) {
  const user = useAppStore(({ user }) => user);

  const [fileList, setFileList] = useState<File[]>([]);

  /**
   * 监听全局粘贴事件，支持粘贴图片上传
   * 排除在输入框中的粘贴事件
   */
  useEventListener("paste", async (event) => {
    // 搜索相似图片弹窗打开时或未登录时不处理
    if (window._search_similar_img_visible || !user) {
      return;
    }

    // 在输入类型组件里不触发粘贴事件
    const target = event.target as HTMLElement;
    if (
      target?.tagName === "INPUT" ||
      target?.tagName === "TEXTAREA" ||
      target.hasAttribute("contenteditable")
    ) {
      return;
    }

    const items = event.clipboardData && event.clipboardData.items;
    if (!items?.length) {
      return;
    }

    let files: File[] = [];

    // 检索剪切板items，提取图片文件
    for (var i = 0; i < items.length; i++) {
      if (items[i].type.startsWith("image/")) {
        const file = items[i].getAsFile();
        if (file) {
          files.push(file);
        }
      }
    }

    if (files.length > 0) {
      setFileList(files);
    }
  });

  // 文件上传相关逻辑
  const { uploadResources, setUploadResources, updateResource, onFileUpload } =
    useFileUpload();

  // 资源选择逻辑
  const [selected, setSelected] = useListSelect(uploadResources, "uid");

  // 当文件列表变化时，触发上传
  useAsyncEffect(async () => {
    if (fileList.length > 0) {
      const resources = await onFileUpload(fileList);
      setFileList([]);
      setSelected(resources?.[0]); // 自动选中第一张
    }
  }, [fileList, onFileUpload]);

  // Pin 创建相关逻辑
  const { onCreatePins, onTagsChange, tagResult, globalValue, setGlobalValue } =
    usePinCreateForm({
      resources: uploadResources,
      setResources: setUploadResources,
      onSucess,
    });

  /**
   * 移除上传资源
   */
  const onRemove = useMemoizedFn((resource: UploadResource) => {
    setUploadResources(
      uploadResources.filter((item) => item.uid !== resource.uid)
    );
  });

  return {
    selected,
    setSelected,
    uploadResources,
    fileList,
    setFileList,
    onRemove,
    onCreatePins,
    onTagsChange,
    tagResult,
    globalValue,
    setGlobalValue,
    updateResource,
  };
}

/**
 * 上传资源接口定义
 * 包含文件信息、上传状态、表单数据和 AIGC 信息
 */
interface UploadResource {
  uid: string; // 唯一标识符（name + size + lastModified）
  size: number; // 文件大小
  name: string; // 文件名
  url: string; // 预览 URL（Base64）
  status: number; // 上传状态
  progress: number; // 上传进度（0-100）
  file: File; // 原始文件对象
  description: string; // 描述
  tags: string[]; // 标签数组
  link: string; // 来源链接
  activity_id?: string; // 活动 ID
  ossFile?: IPinFile & {
    // OSS 上传后返回的文件信息
    aigc?: {
      aigc_category?: string; // AI 工具分类
      prompt?: string; // AI 提示词
    };
  };
  failMsg?: string; // 失败消息

  // 用户手动勾选的 AIGC 标记
  user_checked_aigc: boolean;
  // 用户输入的 AIGC 信息（可能与 ossFile.aigc 不同）
  aigc: {
    aigc_category?: string;
    prompt?: string;
  };
}

/**
 * 文件上传 Hook
 *
 * @description
 * 管理文件上传的核心逻辑，包括：
 * - 文件验证（大小、数量、重复）
 * - 并发上传控制（最多5个并发）
 * - 上传进度跟踪
 * - 错误处理
 * - 取消上传
 *
 * @returns 上传资源管理相关的状态和方法
 */
function useFileUpload() {
  // 防抖提示消息，避免频繁弹窗
  const { run: tipInfo } = useDebounceFn(HBMessage.info, { wait: 500 });
  const { run: tipError } = useDebounceFn(HBMessage.error, { wait: 500 });
  const update = useUpdate(); // 强制更新组件

  const [uploadResources, setUploadResources] = useState<UploadResource[]>([]);
  // 保存 AbortController 用于取消上传
  const abortControllersRef = useRef<(AbortController | null)[]>([]);

  /**
   * 执行上传任务
   * 使用并发控制，最多同时上传 5 个文件
   */
  const execUploadTasks = useMemoizedFn((resources: UploadResource[]) => {
    parallel(
      resources.map((resource) => async () => {
        const { uid, file } = resource;

        // 跳过非新状态的资源
        if (resource.status !== UPLOAD_STATUS.NEW) {
          return;
        }

        resource.status = UPLOAD_STATUS.UPLOADING;
        resource.progress = 0;
        update();

        try {
          // 创建 AbortController 用于取消上传
          const controller = window.AbortController
            ? new AbortController()
            : null;
          abortControllersRef.current.push(controller);

          // 上传文件，并更新进度
          const result = await uploadFile(
            file,
            ({ loaded, total }) => {
              resource.progress = (loaded / total) * 100;
              update();
            },
            controller?.signal
          );

          if (!result.err) {
            resource.ossFile = result;
            resource.aigc = { ...(resource.ossFile?.aigc || {}) };
            resource.status = UPLOAD_STATUS.DONE;
            // 如果 OSS 返回了 AIGC 信息，自动勾选 AIGC 标记
            resource.user_checked_aigc = !!resource.ossFile?.aigc;
          } else {
            throw new Error("上传文件失败:" + result.err.toString());
          }
          update();
        } catch (error) {
          resource.status = UPLOAD_STATUS.FAILURE;
          gdSlsReportError(
            new Error("上传文件失败:" + (error as Error).toString())
          );
          update();
        }
      }),
      5 // 最多 5 个并发
    );
  });

  /**
   * 组件卸载时停止所有上传任务
   */
  useUnmount(() => {
    abortControllersRef.current?.forEach((controller) => {
      controller?.abort();
    });
  });

  /**
   * 文件上传处理
   * 验证文件、创建资源对象、触发上传任务
   *
   * @param fileList - 待上传的文件列表
   * @returns 创建的资源数组
   */
  const onFileUpload = useMemoizedFn(async (fileList: File[]) => {
    const resources: UploadResource[] = [];

    // 检查用户登录状态，未登录时打开绑定手机号弹窗
    const user = await openBindTelModal();

    if (!user) {
      tipError("请先登录");
      return;
    }

    for (const file of fileList) {
      const { name, size, lastModified } = file;
      // 使用文件信息生成唯一 ID
      const uid = name + size + lastModified;

      // 检查重复
      const hasUpload = uploadResources.find((r) => r.uid === uid);
      if (hasUpload) {
        tipInfo("已帮你过滤重复图片");
        continue;
      }

      // 检查文件大小（不超过 20MB）
      if (size >= 20 * 1024 * 1024) {
        tipError(`图片【${name}】太大，单张图片体积不超过 20 MB`);
        continue;
      }

      // 检查数量限制
      if (resources.length + uploadResources.length >= MaxUploadAmount) {
        tipInfo(`只能上传${MaxUploadAmount}张图片`);
        break;
      }

      // 创建上传资源对象
      const resource: UploadResource = {
        uid,
        name,
        size,
        progress: 0,
        status: UPLOAD_STATUS.NEW,
        url: "",
        file,
        description: name.split(".").shift() as string, // 默认描述为文件名（不含扩展名）
        tags: [],
        link: "",
        activity_id: undefined,
        ossFile: undefined,

        user_checked_aigc: false,
        aigc: {},
      };

      // 使用 FileReader 生成预览 URL
      const reader = new FileReader();
      reader.addEventListener("load", function () {
        resource.url = this.result as string;
        update(); // 更新视图
      });
      reader.readAsDataURL(file);
      resources.push(resource);
    }

    // 新资源添加到列表开头
    setUploadResources((prev) => resources.concat(prev));
    // 开始上传任务
    execUploadTasks(resources);
    return resources;
  });

  /**
   * 更新单个资源的属性
   * @param target - 目标资源
   * @param values - 要更新的值
   */
  const updateResource = useMemoizedFn(
    (target: UploadResource, values: Partial<UploadResource>) => {
      setUploadResources(
        uploadResources.map((resource) => {
          if (resource.uid === target.uid) {
            return assign(resource, values);
          }
          return resource;
        })
      );
    }
  );

  return {
    uploadResources,
    setUploadResources,
    updateResource,
    onFileUpload,
  };
}

/**
 * 列表选择 Hook
 *
 * @description
 * 通用的列表项选择逻辑，支持：
 * - 自动选中第一项
 * - 根据 key 查找匹配项
 * - 选中状态管理
 *
 * @param list - 列表数据
 * @param key - 用于匹配的字段名
 * @returns [选中项, 选择函数, 选择第一项函数]
 */
function useListSelect<T extends Record<string, any>, K extends keyof T>(
  list: T[],
  key: K
) {
  const [selected, setSelected] = useState<T | null>(null);

  /**
   * 选中第一项
   */
  const selectFist = useMemoizedFn(() => {
    if (list.length > 0) {
      setSelected(list[0]);
    } else {
      setSelected(null);
    }
  });

  /**
   * 根据选中的 key 值查找对应的项
   */
  const selectedItem = useMemo(() => {
    if (selected) {
      return list.find((item) => item[key] === selected) || list[0] || null;
    }
    return null;
  }, [key, list, selected]);

  /**
   * 自动选中第一项（当列表不为空且无选中项时）
   */
  useEffect(() => {
    if (list.length > 0 && selectedItem === null) {
      setSelected(list[0]?.[key] || null);
    }
  }, [key, list, selectedItem]);

  /**
   * 选择项
   */
  const onSelect = useMemoizedFn((item: T) => {
    setSelected(item?.[key]);
  });

  return [selectedItem, onSelect, selectFist] as const;
}

/**
 * Pin 创建表单 Hook
 *
 * @description
 * 管理 Pin 创建的核心业务逻辑，包括：
 * - 获取用户标签
 * - 画板和标签的全局状态管理
 * - 创建 Pin 的 API 调用
 * - 上传成功/失败处理
 * - 埋点上报
 *
 * @param resources - 上传资源列表
 * @param setResources - 设置资源列表的函数
 * @param onSucess - 创建成功的回调
 * @returns Pin 创建相关的状态和方法
 */
function usePinCreateForm({
  resources,
  setResources,
  onSucess,
}: {
  resources: UploadResource[];
  setResources: (res: UploadResource[]) => void;
  onError?: () => void;
  onSucess?: (board: IBoardInfo) => void;
}) {
  // 使用 ref 保存 Muse 用户状态，避免闭包问题
  const isMuseUserRef = useRef(false);

  const user = useAppStore((s) => s.user);

  /**
   * 获取用户的 Muse 状态
   */
  useAsyncEffect(async () => {
    if (user) {
      const MuseStatus = await getMyMuseStatus();
      const { isMuseUser } = MuseStatus;
      isMuseUserRef.current = isMuseUser;
    }
  }, [user?.user_id]);

  /**
   * 获取用户的标签列表
   */
  const { data: tagResult } = useReq(
    async () => {
      if (user) {
        const urlname = user?.urlname;
        let tagResult = await getTags({ urlname, sort: "often" });
        // 转换 recent 标签格式
        tagResult.recent =
          tagResult?.recent?.map((item: { tag_name: any }) => item.tag_name) ||
          [];

        return tagResult;
      }

      return {
        tags: [],
        recent: [],
      };
    },
    {
      refreshDeps: [user?.user_id],
    }
  );

  // 防止重复提交
  const [canUpload, setCanUpload] = useState(true);

  /**
   * 创建 Pins
   * 将上传资源转换为 API 参数，调用创建接口
   */
  const onCreatePins = useMemoizedFn(async () => {
    if (!canUpload) return;

    const list = [...resources];

    setCanUpload(false);
    const boardId = globalValue.boardId;

    // 验证画板是否选择
    if (!boardId) {
      HBMessage.error("请选择画板");
      setCanUpload(true);
      return;
    }

    // 构建创建 Pin 的参数
    const data = {
      ...(boardId <= 0 ? null : { board_id: boardId }),
      pins: list.map((r) => {
        let aigc = r.aigc || {
          prompt: "",
        };

        // 如果用户未勾选 AIGC，则不传 aigc 字段
        if (!r.user_checked_aigc) {
          aigc = undefined;
        } else {
          aigc.prompt = aigc.prompt || "";
        }

        // 优先使用全局标签，否则使用资源自己的标签
        const tags = globalValue.tags?.length > 0 ? globalValue.tags : r.tags;
        return {
          file_id: r.ossFile?.id,
          text: r.description ?? undefined,
          tags: tags?.length ? tags : undefined,
          aigc,
          link: r.link ?? undefined,
          ...(r.activity_id ? { activity_id: r.activity_id } : {}),
        };
      }),
    };

    // 调用创建 API
    const { err, failMsg, result } = await createPins(
      { isMuseUser: isMuseUserRef.current },
      data
    );

    setCanUpload(true);

    // 处理具体图片的错误
    let errs: string[] = [];
    const nextResources: UploadResource[] = [];
    for (const key in result) {
      const res = result[key];
      if (res.err) {
        // 查找失败的资源
        const matches = resources.filter((r) => r.ossFile?.id === Number(key));
        if (!errs.includes(res.msg)) {
          errs.push(res.msg);
        }
        matches.forEach((r) => {
          r.status = UPLOAD_STATUS.FAILURE;
          r.failMsg = res.msg;
          nextResources.push(r);
        });
      }
    }

    // 埋点上报数据
    const upload_type = "web端";
    const upload_source = "发布页";
    const upload_num = data.pins?.length;

    // 部分失败或全部失败的处理
    if (errs.length) {
      const successNum = resources.length - nextResources.length;
      if (successNum > 0) {
        HBMessage.info(
          `上传成功${successNum}个，失败${nextResources.length}个`,
          5
        );

        uploadTracker({
          upload_status: "部分失败",
          upload_num,
          upload_type,
          upload_source,
        });
      } else {
        errs.forEach((err) => HBMessage.error(err, 5));
        uploadTracker({
          upload_status: "全部失败",
          upload_num,
          upload_type,
          upload_source,
        });
      }
      setResources(nextResources);
      return;
    }

    // 其他失败原因处理
    if (err) {
      setCanUpload(true);
      uploadTracker({
        upload_status: "全部失败",
        upload_num,
        upload_type,
        upload_source,
      });
      return;
    }
    if (failMsg) {
      setCanUpload(true);
      HBMessage.error(failMsg);
      return;
    }

    // 全部成功
    uploadTracker({
      upload_status: "全部成功",
      upload_num,
      upload_type,
      upload_source,
    });

    setResources([]);

    // 获取实际创建的画板 ID（可能是新创建的）
    const realBoardId = Object.values(result)?.find((value) => value?.board_id)
      ?.board_id as number;

    // 获取画板详情，用于显示成功消息
    const { board } = await getBoard({
      board_id: boardId <= 0 ? realBoardId : boardId,
      pins: 1,
    });

    requestAnimationFrame(() => {
      onSucess?.(board);
    });

    // 刷新最近使用的画板列表
    useAppStore.getState().refreshLastBoards();
  });

  /**
   * 全局值状态（画板 ID、标签、标签同步开关）
   */
  const [globalValue, setGlobalValue] = useSetState<{
    boardId?: number;
    tags?: string[];
    syncTagsChecked?: boolean;
  }>({
    tags: [],
  });

  /**
   * 标签变化处理（限制最多 10 个标签）
   */
  const onTagsChange = useMemoizedFn((value) => {
    if (value && value.length > 10) {
      HBMessage.info("最多添加10个标签");
    }
  });

  return {
    tagResult,
    /** 应用全局的变量，比如画板 */
    globalValue,
    setGlobalValue,
    onCreatePins,
    onTagsChange,
  };
}

/**
 * AI 工具选择器组件
 * 用于选择 AI 创作工具分类
 *
 * @param onChange - 选择变化回调
 * @param value - 当前选中的值
 * @param disabled - 是否禁用
 * @param ref - React ref
 */
function ToolSelect({
  onChange,
  value,
  disabled,
  ref,
}: {
  onChange?: (value: string) => void;
  value?: string;
  disabled?: boolean;
  ref?: React.Ref<any>;
}) {
  const { aiLinkMap, setStore } = useAppStore(({ aiLinkMap, setStore }) => ({
    aiLinkMap,
    setStore,
  }));

  const onSelect = (value) => {
    onChange(value);
  };

  /**
   * 初始化 AI 工具列表
   * 从配置接口获取工具列表
   */
  const init = useMemoizedFn(async () => {
    const result = await (!aiLinkMap || aiLinkMap.length === 0
      ? getGrocery("pin_detail_ai_params")
      : undefined);
    if (result) {
      setStore({
        aiLinkMap: [
          ...(result as any).linkMap?.filter((item) => item.isShow),
          { value: "other", name: "其他", isShow: true },
        ],
      });
    }
  });

  useEffect(() => {
    init();
  }, [init]);

  /**
   * 将 aiLinkMap 转换为 Select 组件的 options 格式
   */
  const options = useMemo(() => {
    return aiLinkMap
      ?.filter((item) => item.isShow)
      .map((item) => ({
        label: item.name,
        value: item.value,
      }));
  }, [aiLinkMap]);

  /**
   * 当前选中的值
   * 如果当前值不在 options 中，则返回 "other"
   */
  const currentValue = useMemo(() => {
    const current = options.find((item) => item.value === value);
    return current?.value ?? "other";
  }, [value, options]);

  return (
    <Select
      ref={ref}
      className={classNames(styles.categorySelect, "black-input")}
      value={currentValue}
      onChange={onSelect}
      disabled={disabled}
      options={options}
      placeholder="创作工具"
      loading={!aiLinkMap || aiLinkMap.length === 0}
    ></Select>
  );
}
